import { IS_WEB } from "@common/utils/mobileUtils"

/**
 * L'adresse du live, au web : `/live/<clé>`, et chaque écran du live sous elle
 * (`/live/<clé>/end-session-student`, `/live/<clé>/student?…`). La clé est celle de l'entrée
 * dans le live (`liveMode.entryKey`, tirée au hasard par `setIsLiveModeEnabled`) : elle ne dit ni
 * l'id de la séance ni rien qui se devine.
 *
 * Le live reste un état de l'app : c'est lui qui choisit la pile du live (`RootNavigator`), et
 * l'adresse le suit. Elle ne compte qu'au démarrage, où cet appareil la compare à sa mémoire du
 * live (`liveMemory`) — le serveur ne la lit jamais : `/` mène à l'accueil, jamais dans un live, et
 * une clé que la mémoire ne connaît pas aussi. Hors d'un live, une adresse de live ne désigne rien.
 */
const LIVE_ADDRESS = /^\/?live\/([A-Za-z0-9]+)(?=[/?#]|$)/

/** La clé d'entrée d'une adresse de live (`/live/<clé>/…`, avec ou sans `/` devant), ou null. */
export function liveKeyOfAddress(path) {
    const match = typeof path === "string" ? path.match(LIVE_ADDRESS) : null
    return match ? match[1] : null
}

/** Le chemin de l'écran, sans l'adresse du live devant : `/live/<clé>/x?y` → `/x?y`. */
function withoutLiveAddress(path) {
    const rest = path.replace(LIVE_ADDRESS, "")
    return rest.startsWith("/") ? rest : `/${rest}`
}

/** Le chemin d'un écran du live, sous l'adresse du live : `/x?y` → `/live/<clé>/x?y`, `/` → `/live/<clé>`. */
function underLiveAddress(path, liveKey) {
    const rest = path.replace(/^\/+/, "")
    return `/live/${liveKey}${rest === "" || rest.startsWith("?") ? rest : `/${rest}`}`
}

/** Le chemin de la page chargée, au web ; `null` dans l'app, qui n'a pas d'adresse. */
export function pageAddress() {
    return IS_WEB ? (globalThis.location?.pathname ?? "/") : null
}

/**
 * Les deux traductions adresse ⇄ navigation du web, rendues conscientes du live.
 * `openLiveKey()` : la clé du live ouvert dans cet onglet, ou null.
 *
 * - En live, chaque écran a son adresse sous `/live/<clé>`, et une adresse qui n'est pas sous
 *   celle de ce live ne désigne aucun de ses écrans (`undefined`). React Navigation garde à une
 *   route l'adresse d'où elle est née tant qu'il la relit pareil : sans ce `undefined`, une route
 *   que la pile garde en basculant dans le live (l'accueil né de `/menu/home`) y garderait son
 *   adresse d'avant.
 * - Hors live, une adresse de live ne désigne rien : l'accueil.
 */
export function liveAwareLinking(
    { getStateFromPath, getPathFromState },
    openLiveKey
) {
    return {
        getStateFromPath: (path, options) => {
            const live = openLiveKey()
            // Hors live, `null` contre `null` : seule une adresse sans live se lit.
            if (liveKeyOfAddress(path) !== live) return undefined
            return getStateFromPath(
                live == null ? path : withoutLiveAddress(path),
                options
            )
        },
        getPathFromState: (state, options) => {
            const path = getPathFromState(state, options)
            const live = openLiveKey()
            return live == null ? path : underLiveAddress(path, live)
        },
    }
}
